"use client"

import { BRAND_GROUP_HEADINGS, brandGroupsPresent, brandProductGroup } from "@/lib/brands/product-order"
import * as React from "react"
import { useRouter } from "next/navigation"
import { Pagination } from "@/components/brand-kit/pagination"
import { ProductGridCard } from "@/components/brand-kit/product-grid-card"
import { formatPrice } from "@/lib/format-utils"
import { productPagePath } from "@/lib/parts/fpn"
import type { BrandProduct, ConcreteCatalogType } from "./types"
import { brandProductBadges } from "./types"
import { nonFinRowIdentifier } from "@/lib/card-link-names"

interface ProductGridProps {
  products: BrandProduct[]
  brandName: string
  currentPage: number
  totalPages: number
  /**
   * Catalog URL state, resolved on the server and passed down rather than
   * read from `useSearchParams()`. Reading it here bailed the page-wide
   * Suspense boundary out of the static prerender, so the canonical URL
   * served a spinner to every non-JS crawler (FRIDGE-237). The route reads
   * exactly two params — `page` and `type` — so rebuilding the query from
   * these two props is lossless.
   */
  basePath: string
  activeTypes: ConcreteCatalogType[]
}

/**
 * Catalog product grid for the brand detail page.
 *
 * Uses the brand-kit `ProductGridCard` (no dots, brand variant) so the
 * cards match every other hub/brand-style grid sitewide. Pagination is
 * URL-bound — clicking a page rewrites `?page=N` while preserving the
 * `?type=X` filter, and scrolls back up to the catalog anchor so users
 * land on the first card of the next page.
 *
 * Price contract: when a product has no `lowest_price_cents`, no
 * placeholder is shown ("—", "$0", "N/A" are all suppressed); the price
 * slot just renders empty. Same for the strike-through average price.
 */
export function ProductGrid({
  products,
  brandName,
  currentPage,
  totalPages,
  basePath,
  activeTypes,
}: ProductGridProps) {
  const router = useRouter()

  // Build the real ?page=N URL for a page, preserving the ?type=X filter — used
  // both for client nav and as the crawlable href so Googlebot can reach deep
  // brand-catalog pages (they were button-only = invisible to crawlers before).
  const hrefForPage = React.useCallback(
    (next: number) => {
      const params = new URLSearchParams()
      if (activeTypes.length > 0) params.set("type", activeTypes.join(","))
      if (next > 1) params.set("page", String(next))
      const qs = params.toString()
      return qs ? `${basePath}?${qs}` : basePath
    },
    [basePath, activeTypes],
  )

  const goToPage = React.useCallback(
    (next: number) => {
      router.push(hrefForPage(next), { scroll: false })
      if (typeof window !== "undefined") {
        const target = document.getElementById("brand-catalog")
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    },
    [router, hrefForPage],
  )

  if (products.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center">
        <p className="text-[15px] font-semibold text-gray-900">No products match this filter.</p>
        <p className="mt-1 text-[13px] text-gray-500">
          Try clearing the type filter to see the full {brandName} catalog.
        </p>
      </div>
    )
  }

  return (
    <>
      {/* Mobile renders 2 cols so visitors see ~4 cards in the viewport
         (was a single-column stack that buried the 2nd card below the
         fold). Desktop scales back up to the standard hub-page rhythm:
         3 cols on md, 4 on lg. Gap tightens on mobile to keep two cards
         readable at 375px width. */}
      {brandGroupsPresent(products).map(group => (
      <section key={group} className="mt-6" aria-label={`${brandName} ${BRAND_GROUP_HEADINGS[group]}`}>
      <h3 className="mb-4 text-xl font-semibold text-gray-900">{brandName} {BRAND_GROUP_HEADINGS[group]}</h3>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
        {products.filter(p => brandProductGroup(p) === group).map(p => {
          const hasPrice = !!(p.lowest_price_cents && p.lowest_price_cents > 0)
          // Strike-through shows on ANY drop below the 30-day average — matches
          // the canonical price_drop rule (spec §4.1 retired the old ≥10%
          // threshold) and the brand carousels, so the price row and the
          // canonical badge agree.
          const hasDrop = !!(
            hasPrice
            && p.average_price_30d_cents
            && p.average_price_30d_cents > (p.lowest_price_cents as number)
          )
          // Family-aware first: filter rows link /filter/<slug>, parts
          // /part/<slug> (stored canonical_url predates the family split for
          // those rows and would link a redirect). Appliances are unchanged.
          const href = productPagePath(p) || p.canonical_url || `/products/${p.seo_slug || p.id}`
          // Only forward `price` / `originalPrice` when we actually have
          // numbers — passing `undefined` is the documented signal to
          // ProductGridCard to omit the price/strike row entirely (no "—"
          // or "$0" placeholders). Same rule for the strike.
          return (
            <ProductGridCard
              key={p.id}
              images={Array.isArray(p.images) ? p.images : []}
              brand={brandName}
              title={p.title}
              href={href}
              price={hasPrice ? formatPrice((p.lowest_price_cents as number) / 100, true) : undefined}
              originalPrice={hasDrop ? formatPrice((p.average_price_30d_cents as number) / 100, true) : undefined}
              badges={brandProductBadges(p)}
              // FRIDGE-1150: filter / part URLs carry no FIN; the row's model,
              // FFN or FPN keeps same-titled cards' link names apart.
              identifier={nonFinRowIdentifier(href, p)}
              // Owner design rule 6 and FRIDGE-1085's order: the brand page's
              // filters show their FFN and its parts their FPN. A row carries
              // one or neither (the database mints each for its family only).
              familyNumber={p.ffn || p.fpn || null}
              // FRIDGE-1279: appliance rows show FIN (from the URL) and model.
              model={p.model ?? null}
            />
          )
        })}
      </div>
      </section>
      ))}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
          hrefForPage={hrefForPage}
          className="!mt-10"
        />
      )}
    </>
  )
}
